import { forwardRef, memo, useImperativeHandle, useMemo } from 'react';
import { DateTime } from 'luxon';
import RequiredAsterisk from '../RequiredAsterisk';
import { TranslationService } from '../../../services/translation.service';
import { DateHelper } from '../../../helpers/date.helper';
import type IValidatable from '../validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import type { IServerValidationError } from '../validatable.interface';
import { IdHelper } from '../../../helpers/id.helper';

export interface IDatePickerState {
  value: string;
  validationInfo: ValidationInfo;
}

interface IDatePickerProps {
  minDate?: string;
  maxDate?: string;
  showAsteriskOnRequiredFields?: boolean;
  ariaDescribedBy?: string | undefined;
  labelText?: string;
  className?: string | undefined;
  serverValidationName?: string | undefined;
  state: IDatePickerState;
  onChange?: ((date: string) => void) | undefined;
}

function createOptionsArray(minDate: DateTime, maxDate: DateTime): DateTime[] {
  const result = [];
  let d = minDate;
  const max = maxDate;
  while (d <= max) {
    result.push(d);
    d = d.plus({ days: 1 });
  }
  return result;
}

const SelectOptions = memo(function SelectOptions({ dateOptions, selectedValue }: { dateOptions: DateTime[]; selectedValue: string }) {
  return (
    <>
      {dateOptions.map((val) => {
        const dateString = val.toFormat(DateHelper.DATE_FORMAT);
        return (
          <option key={dateString} className={`date-option ${selectedValue === dateString ? 'selected' : ''}`} value={dateString}>
            {DateHelper.formatWithWeekday(val)}
          </option>
        );
      })}
    </>
  );
});

export default forwardRef<IValidatable, IDatePickerProps>(function DatePicker(
  {
    minDate = DateTime.now().startOf('day').minus({ days: 30 }).toFormat(DateHelper.DATE_FORMAT),
    maxDate = DateTime.now().startOf('day').plus({ days: 30 }).toFormat(DateHelper.DATE_FORMAT),
    showAsteriskOnRequiredFields = false,
    ariaDescribedBy = undefined,
    labelText = undefined,
    className = undefined,
    serverValidationName = 'date',
    state,
    onChange,
  }: IDatePickerProps,
  ref
) {
  // === MEMOS ===
  const dateOptions = useMemo(() => createOptionsArray(DateTime.fromFormat(minDate, DateHelper.DATE_FORMAT), DateTime.fromFormat(maxDate, DateHelper.DATE_FORMAT)), [minDate, maxDate]);

  // === VARIABLES ===
  const elementId = IdHelper.newUniqueId('date-select-');
  const labelTextWithFallback = labelText ?? TranslationService.get('common.day');

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      return new ValidationInfo(`${labelText} - `, []);
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  // === EVENT HANDLERS ===
  function handleSelectChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    onChange?.(e.target.value);
  }

  return (
    <div className={`date-picker w-100 ${state.validationInfo.isInvalid ? 'is-invalid' : ''} ${className ?? ''}`}>
      <label htmlFor={elementId} className="form-label">
        {labelTextWithFallback}
        {showAsteriskOnRequiredFields && <RequiredAsterisk />}
      </label>
      <select
        id={elementId}
        value={state.value}
        className={`w-100 ms-0 me-0 form-select ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
        aria-invalid={state.validationInfo.isInvalid ? 'true' : 'false'}
        aria-describedby={ariaDescribedBy}
        onChange={handleSelectChange}
      >
        <SelectOptions dateOptions={dateOptions} selectedValue={state.value} />
      </select>
      {state.validationInfo.getErrors().map((error) => (
        <span id="date-error" key={error} className="invalid-feedback">
          {error}
        </span>
      ))}
    </div>
  );
});
